import * as React from 'react';
import { Radio, Select, Space } from 'antd';

export interface PropagationRadioGroupProps {
  value?: Propagation;
  stepOptions?: GetProp<typeof Select<Propagation>, 'options'>;
  autoSelectFirstStep?: boolean;
  onChange?: (value: NonNullable<PropagationRadioGroupProps['value']>) => void;
}

const PropagationRadioGroup = (props: PropagationRadioGroupProps) => {
  const { value, stepOptions = [], autoSelectFirstStep, onChange } = props;
  const [radioGroupValue, setRadioGroupValue] = React.useState(
    value?.startsWith('step_') ? 'step' : value,
  );
  const stepValue = value?.startsWith('step_') ? (value as `step_${number}`) : undefined;
  const radioGroupOptions = React.useMemo<GetProp<typeof Radio.Group, 'options'>>(() => {
    return [
      {
        value: 'global',
        label: 'First',
      },
      {
        value: 'context',
        label: 'Last',
      },
      {
        value: 'step',
        disabled: !stepOptions.length,
        label: 'Step',
      },
    ];
  }, [stepOptions]);
  const selectOptions = React.useMemo<GetProp<typeof Select, 'options'>>(
    () =>
      stepOptions.map((item, index) => ({
        value: `step_${index + 1}`,
        label: index + 1,
      })),
    [stepOptions],
  );

  const handleRadioGroupChange = React.useCallback<GetProp<typeof Radio.Group, 'onChange'>>(
    (e) => {
      const { value } = e.target;

      setRadioGroupValue(value);

      if (value === 'step') {
        if (autoSelectFirstStep) {
          // Default to first step if switching to step propagation
          onChange?.('step_1');
        }

        return;
      }

      return onChange?.(value);
    },
    [onChange, autoSelectFirstStep],
  );
  const handleSelectChange = React.useCallback<
    GetProp<typeof Select<NonNullable<typeof stepValue>>, 'onChange'>
  >(
    (selectValue) => {
      setRadioGroupValue('step');

      return onChange?.(selectValue);
    },
    [onChange],
  );
  const selectLabelRender = React.useCallback<GetProp<typeof Select, 'labelRender'>>((p) => {
    return p.label ?? (p.value as string).replace('step_', '');
  }, []);

  React.useEffect(() => {
    setRadioGroupValue(value?.startsWith('step_') ? 'step' : value);
  }, [value]);

  return (
    <Space size={4}>
      <Radio.Group
        value={radioGroupValue}
        options={radioGroupOptions}
        onChange={handleRadioGroupChange}
      />
      <Select
        value={stepValue}
        disabled={!selectOptions.length}
        placeholder="Select step"
        options={selectOptions}
        labelRender={selectLabelRender}
        style={{ width: 120 }}
        onChange={handleSelectChange}
      />
    </Space>
  );
};

export default React.memo(PropagationRadioGroup);
